import { type QueryKey, skipToken, useInfiniteQuery } from '@tanstack/react-query';
import { isObject } from 'lodash';

import { Agents } from '@air/api';
import { callAirApi } from '@air/utils-api';

export const PRIVATE_WORKSPACE_AGENT_SESSION_MESSAGES_INFINITE_KEY =
  'PRIVATE_WORKSPACE_AGENT_SESSION_MESSAGES_INFINITE';

export type PrivateWorkspaceAgentSessionMessagesInfiniteParams = Omit<
  NonNullable<Parameters<typeof Agents.Sessions.Messages.list>[0]>,
  'agentType' | 'workspaceId' | 'sessionId' | 'cursor'
> & {
  workspaceId: string | undefined;
  sessionId: string | undefined;
};

export type PrivateWorkspaceAgentSessionMessagesInfiniteKey = [
  typeof PRIVATE_WORKSPACE_AGENT_SESSION_MESSAGES_INFINITE_KEY,
  PrivateWorkspaceAgentSessionMessagesInfiniteParams,
];

export const getPrivateWorkspaceAgentSessionMessagesInfiniteKey = (
  params: PrivateWorkspaceAgentSessionMessagesInfiniteParams,
): PrivateWorkspaceAgentSessionMessagesInfiniteKey => [PRIVATE_WORKSPACE_AGENT_SESSION_MESSAGES_INFINITE_KEY, params];

export const isPrivateWorkspaceAgentSessionMessagesInfiniteKey = (
  key: QueryKey,
): key is PrivateWorkspaceAgentSessionMessagesInfiniteKey =>
  Array.isArray(key) && key[0] === PRIVATE_WORKSPACE_AGENT_SESSION_MESSAGES_INFINITE_KEY && isObject(key[1]);

export type UsePrivateWorkspaceAgentSessionMessagesInfiniteOptions = {
  enabled?: boolean;
};

export const usePrivateWorkspaceAgentSessionMessagesInfinite = (
  params: PrivateWorkspaceAgentSessionMessagesInfiniteParams,
  options?: UsePrivateWorkspaceAgentSessionMessagesInfiniteOptions,
) => {
  const { workspaceId, sessionId } = params;

  const queryFn =
    !workspaceId || !sessionId
      ? skipToken
      : ({ pageParam }: { pageParam: string }) =>
          callAirApi(() =>
            Agents.Sessions.Messages.list({
              agentType: 'workspace',
              ...params,
              workspaceId,
              sessionId,
              cursor: pageParam || undefined,
            }),
          );

  return useInfiniteQuery({
    queryKey: getPrivateWorkspaceAgentSessionMessagesInfiniteKey(params),
    queryFn,
    initialPageParam: '',
    getNextPageParam: (lastPage) => (lastPage.pagination?.hasMore ? (lastPage.pagination.cursor ?? null) : null),
    enabled: !!workspaceId && !!sessionId && (options?.enabled ?? true),
  });
};
